import { fahrenheitToCelcius, simpleAve } from '../../utils/helpers'

function detGdd(dailyData, startDay, base) {
  let gdd = 0
  return dailyData.slice(startDay).map((day) => {
    const tempC = day.temp.map((t) => fahrenheitToCelcius(t))
    const mintC = Math.min(...tempC)
    const maxtC = Math.max(...tempC)
    const dd = simpleAve(mintC, maxtC, base)

    return {
      date: day.date,
      isForecast: day.isForecast,
      ms: day.ms,
      serverDate: day.serverDate,
      dd,
      gdd: (gdd += dd),
    }
  })
}

function development(day, threshold, key) {
  const { gdd } = day

  const ddRemaining =
    threshold - gdd <= 0 ? 'Complete' : Math.round(threshold - gdd)
  return {
    ...day,
    [`${key}DDRemaining`]: ddRemaining,
    emergence: gdd >= threshold ? true : false,
  }
}

export default function modelLogic(data, biofix) {
  if (!data || !biofix) return
  const { dailyData } = data

  const biofixDate = dailyData.find((day) => day.serverDate === biofix)

  const idx1 = dailyData.findIndex((day) => day.serverDate === biofix)
  if (idx1 === -1) return

  const eggDiapause = detGdd(dailyData, idx1, 7.36).map((day) =>
    development(day, 170, 'diapause'),
  )

  let idx2 = -1
  let date2 = null
  const obj2 = eggDiapause.find((day) => day.emergence)
  if (obj2) {
    idx2 = dailyData.findIndex((day) => day.serverDate === obj2.serverDate)
    date2 = obj2.serverDate
  }

  let eggDev = []
  if (idx2 > -1) {
    eggDev = detGdd(dailyData, idx2, 7.36).map((day) =>
      development(day, 455, 'dev'),
    )
  }

  let idx3 = -1
  let date3 = null
  const obj3 = eggDev.find((day) => day.emergence)
  if (obj3) {
    idx3 = dailyData.findIndex((day) => day.serverDate === obj3.serverDate)
    date3 = obj3.serverDate
  }

  const eggDevDates = [date2, date3].filter(Boolean)
  let eggDevEndIdx = eggDiapause.length
  if (eggDevDates.length === 2)
    eggDevEndIdx = eggDiapause.findIndex((d) => d.serverDate === eggDevDates[1])

  const eggDiapauseDev = eggDiapause.slice(0, eggDevEndIdx + 1).map((day) => {
    const dev = eggDev.find((d) => d.serverDate === day.serverDate)

    let p = {
      serverDate: day.serverDate,
      isForecast: day.isForecast,
      diapauseDDRemaining: day.diapauseDDRemaining,
      devDDRemaining: 'Not Started',
    }

    if (dev) {
      p.devDDRemaining = dev.devDDRemaining
    }

    return p
  })

  // Instar --------------------------------------------------------------
  let instar1 = []
  if (idx3 > -1) {
    instar1 = detGdd(dailyData, idx3, 13).map((day) =>
      development(day, 131, 'instar1'),
    )
  }

  let idx4 = -1
  let date4 = null
  const obj4 = instar1.find((day) => day.emergence)
  if (obj4) {
    idx4 = dailyData.findIndex((day) => day.serverDate === obj4.serverDate)
    date4 = obj4.serverDate
  }

  let instar2 = []
  if (idx4 > -1) {
    instar2 = detGdd(dailyData, idx4, 12.43).map((day) =>
      development(day, 151, 'instar2'),
    )
  }

  let idx5 = -1
  let date5 = null
  const obj5 = instar2.find((day) => day.emergence)
  if (obj5) {
    idx5 = dailyData.findIndex((day) => day.serverDate === obj5.serverDate)
    date5 = obj5.serverDate
  }

  let instar3 = []
  if (idx5 > -1) {
    instar3 = detGdd(dailyData, idx5, 8.48).map((day) =>
      development(day, 280, 'instar3'),
    )
  }

  let idx6 = -1
  let date6 = null
  const obj6 = instar3.find((day) => day.emergence)
  if (obj6) {
    idx6 = dailyData.findIndex((day) => day.serverDate === obj6.serverDate)
    date6 = obj6.serverDate
  }

  let instar4 = []
  if (idx6 > -1) {
    instar4 = detGdd(dailyData, idx6, 6.29).map((day) =>
      development(day, 478, 'instar4'),
    )
  }

  let idx7 = -1
  let date7 = null
  const obj7 = instar4.find((day) => day.emergence)
  if (obj7) {
    idx7 = dailyData.findIndex((day) => day.serverDate === obj7.serverDate)
    date7 = obj7.serverDate
  }

  const instarDates = [date4, date5, date6, date7].filter(Boolean)
  let instarEndIdx = eggDiapause.length
  if (instarDates.length === 4)
    instarEndIdx = eggDiapause.findIndex((d) => d.serverDate === instarDates[3])

  const instar = eggDiapause
    .slice(eggDevEndIdx, instarEndIdx + 1)
    .map((day) => {
      const in1 = instar1.find((d) => d.serverDate === day.serverDate)
      const in2 = instar2.find((d) => d.serverDate === day.serverDate)
      const in3 = instar3.find((d) => d.serverDate === day.serverDate)
      const in4 = instar4.find((d) => d.serverDate === day.serverDate)

      let p = {
        serverDate: day.serverDate,
        isForecast: day.isForecast,
        instar1DDRemaining: 'Not Started',
        instar2DDRemaining: 'Not Started',
        instar3DDRemaining: 'Not Started',
        instar4DDRemaining: 'Not Started',
      }

      if (in1) {
        p.instar1DDRemaining = in1.instar1DDRemaining
      }
      if (in2) {
        p.instar2DDRemaining = in2.instar2DDRemaining
      }
      if (in3) {
        p.instar3DDRemaining = in3.instar3DDRemaining
      }
      if (in4) {
        p.instar4DDRemaining = in4.instar4DDRemaining
      }

      return p
    })

  // Adult Emergence Summary
  let adultEmergence = []
  let completeAdultEmergence = null
  if (idx7 > -1) {
    adultEmergence = detGdd(dailyData, idx7, 6.29).map((d) => {
      const x = Math.round((d.gdd - 419.17) / 4.55)

      let status = ''
      if (x > 100) {
        status = 'Complete'
      } else if (x <= 100 && x > 0) {
        status = 'In Progress'
      } else {
        status = 'Not Started'
      }

      return {
        ms: d.ms,
        serverDate: d.serverDate,
        isForecast: d.isForecast,
        emergence: x < 0 ? 0 : x >= 100 ? 100 : x,
        status,
      }
    })
    completeAdultEmergence = adultEmergence.find((d) => d.status === 'Complete')
  }

  const eggDiapauseDevCSV = eggDiapauseDev.map((d) => ({
    date: d.serverDate,
    [`diapause - (base 7.36C˚)`]: d.diapauseDDRemaining,
    [`development - (base 7.36C˚)`]: d.devDDRemaining,
  }))

  const instarCSV = instar.map((d) => ({
    date: d.serverDate,
    [`instar1 - (base 13.C˚)`]: d.instar1DDRemaining,
    [`instar2 - (base 12.43.C˚)`]: d.instar2DDRemaining,
    [`instar3 - (base 8.48.C˚)`]: d.instar3DDRemaining,
    [`instar4 - (base 6.29.C˚)`]: d.instar4DDRemaining,
  }))

  const adultEmergenceCSV = adultEmergence.map((d) => ({
    date: d.serverDate,
    status: d.status,
    emergence: `${d.emergence}%`,
  }))

  const lastDate = dailyData.slice(-1)[0]
  const dates = [
    biofix,
    date2,
    date3,
    date4,
    date5,
    date6,
    date7,
    lastDate.serverDate,
  ].map((d) => {
    if (d) {
      let ms = null
      const found = dailyData.find((dd) => dd.serverDate === d)
      if (found) ms = found.ms
      return { date: d, ms }
    }
    return d
  })
  // console.log(dates)

  const firstNullIdx = dates.findIndex((d) => d === null)
  const graphData = dates.map((date, i, arr) => {
    let stage = ''
    let status = 'Not Started'
    let ms = biofixDate.ms
    let completed = biofixDate.ms

    if (i === 0) {
      stage = 'Biofix'
      status = 'Complete'
      ms = biofixDate.ms
    }

    if (i === 1) {
      stage = 'Egg Dia'
      if (date) {
        ms = date.ms
        // completed = arr[i - 1].ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 1) {
          ms = lastDate.ms
          // completed = arr[i - 1].ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
          // completed = arr[i - 1].ms
        }
      }
    }

    if (i === 2) {
      stage = 'Egg Dev'
      if (date) {
        ms = date.ms
        // completed = date.ms - arr[i - 1].ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 2) {
          ms = lastDate.ms
          // completed = lastDate.ms - arr[i - 1].ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
          // completed = biofixDate.ms - arr[i - 1].ms
        }
      }
    }

    if (i === 3) {
      stage = 'Instar 1'
      if (date) {
        ms = date.ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 3) {
          ms = lastDate.ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
        }
      }
    }

    if (i === 4) {
      stage = 'Instar 2'
      if (date) {
        ms = date.ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 4) {
          ms = lastDate.ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
        }
      }
    }

    if (i === 5) {
      stage = 'Instar 3'
      if (date) {
        ms = date.ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 5) {
          ms = lastDate.ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
        }
      }
    }

    if (i === 6) {
      stage = 'Instar 4'
      if (date) {
        ms = date.ms
        status = 'Complete'
      } else {
        if (firstNullIdx === 6) {
          ms = lastDate.ms
          status = 'In Progress'
        } else {
          ms = biofixDate.ms
        }
      }
    }

    if (i === 7) {
      stage = 'Adult'
      if (arr[6]) {
        if (completeAdultEmergence) {
          ms = completeAdultEmergence.ms
          status = completeAdultEmergence.status
        } else {
          const lastAdultEmergence = adultEmergence.slice(-1)[0]
          if (
            lastAdultEmergence &&
            lastAdultEmergence.status === 'Not Started'
          ) {
            ms = lastAdultEmergence.ms
            status = lastAdultEmergence.status
          } else {
            ms = date.ms
            status = 'In Progress'
          }
        }
      } else {
        ms = biofixDate.ms
      }
    }

    return {
      stage,
      status,
      ms,
      completed,
    }
  })
  // console.log(graphData)

  return {
    biofixDate,
    eggDiapauseDev,
    eggDiapauseDevCSV,
    instar,
    instarCSV,
    adultEmergence,
    adultEmergenceCSV,
    eggDiapauseDate: date2,
    eggDevDate: date3,
    instar1Date: date4,
    instar2Date: date5,
    instar3Date: date6,
    instar4Date: date7,
    completeAdultEmergence,
    graphData,
  }
}
